import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { getProductDetailsForUrl, findProductByNames } from '../services/urlService';
import { fetchProductById, fetchProductByProductId } from '../services/productService';
import { fetchCollectionById } from '../services/collectionService';
import ShopDetailsBreadcumb from '../components/shopdetails/ShopDetailsBreadcumb';
import DynamicShopDetailsArea from '../components/shopdetails/DynamicShopDetailsArea';
import ShopDetailsSkeleton from '../components/shopdetails/ShopDetailsSkeleton';
import { useShop } from '../context/ShopContext';

function NewShopDetailsPage() {
  const { productSlug } = useParams();
  const history = useHistory();
  const { products: contextProducts, collections: contextCollections, loading: contextLoading } = useShop();

  const [product, setProduct] = useState(null);
  const [collection, setCollection] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for context to be ready (at least from cache)
    if (contextLoading && !contextProducts.length) return;

    const loadProductDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        // 1. Try to find in Context (Cache)
        let foundProduct = contextProducts.find(p => p.productId === productSlug);
        if (!foundProduct) foundProduct = contextProducts.find(p => p.id === productSlug);
        // Fallback check for URL/Name based on old logic if needed, but usually ID or ProductID is used.

        if (foundProduct) {
          console.log('Found product in cache:', foundProduct.name);
          setProduct(foundProduct);

          if (foundProduct.collection) {
            setCollection(foundProduct.collection);
          } else if (foundProduct.collectionId) {
            const col = contextCollections.find(c => c._id === foundProduct.collectionId);
            setCollection(col);
          }
          setLoading(false);
          return;
        }

        console.log('Product not in cache, fetching from API...');

        // 2. Fetch from API if not in cache
        // NEW APPROACH: Try to find by productId first (new randomly generated ID)
        const productByProductId = await fetchProductByProductId(productSlug);
        if (productByProductId.success && productByProductId.data) {
          setProduct(productByProductId.data);

          // Try to get collection if available
          if (productByProductId.data.collectionId) {
            const collectionResponse = await fetchCollectionById(productByProductId.data.collectionId);
            if (collectionResponse.success) {
              setCollection(collectionResponse.data);
            }
          }
        } else {
          // If not found by productId, try the old URL parsing format (fallback for old URLs)
          const productDetails = await getProductDetailsForUrl(productSlug);

          if (productDetails && productDetails.product) {
            // Transform the product data to match the expected format
            // Note: transformProductData is defined below, ensure it's accessible or moved
            const transformedProduct = await transformProductData(productDetails.product);
            setProduct(transformedProduct);
            setCollection(productDetails.collection);
          } else {
            // If not found by URL, try to find by ID (final fallback)
            const productById = await fetchProductById(productSlug);
            if (productById.success && productById.data) {
              setProduct(productById.data);

              // Try to get collection if available
              if (productById.data.collectionId) {
                const collectionResponse = await fetchCollectionById(productById.data.collectionId);
                if (collectionResponse.success) {
                  setCollection(collectionResponse.data);
                }
              }
            } else {
              setError('Product not found');
            }
          }
        }
      } catch (err) {
        console.error('Error loading product details:', err);
        setError(err.message || 'Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    loadProductDetails();
  }, [productSlug, contextLoading, contextProducts, contextCollections]);

  // Helper function to transform product data (similar to productService)
  const transformProductData = async (backendProduct) => {
    // This is a simplified version - you might want to move this to a shared utility
    const colors = [
      { id: 'green', label: 'Green', hex: '#8ab76d' },
      { id: 'gray', label: 'Gray', hex: '#9b9b9b' },
      { id: 'light_gray', label: 'Light Gray', hex: '#9b9b9b' },
      { id: 'pink', label: 'Pink', hex: '#ee8989' },
      { id: 'black', label: 'Black', hex: '#000000' },
      { id: 'brique', label: 'Red Brick', hex: '#cb5130' },
      { id: 'red', label: 'Red', hex: '#DC143C' },
    ];

    const colorNameMap = {
      'Vert': 'green',
      'Rouge Brique': 'brique',
      'Rose': 'pink',
      'Black': 'black',
      'Red': 'red',
      'Gray': 'gray',
      'Green': 'green',
      'Pink': 'pink'
    };

    // Get available colors from colorImages
    const availableColors = backendProduct.colorImages?.map(colorGroup => {
      const colorId = colorNameMap[colorGroup.name] || colorGroup.name.toLowerCase();
      let colorInfo = colors.find(c => c.id === colorId);

      if (!colorInfo) {
        colorInfo = {
          id: colorId,
          label: colorGroup.name,
          hex: '#9b9b9b'
        };
      } else {
        colorInfo = { ...colorInfo, label: colorGroup.name };
      }

      colorInfo.imagePath = colorGroup.images?.[0] || '';
      return colorInfo;
    }) || [];

    if (availableColors.length === 0 && backendProduct.colorImages?.length === 0) {
      availableColors.push({
        id: 'default',
        label: 'Default',
        hex: '#9b9b9b',
        imagePath: backendProduct.images?.[0] || ''
      });
    }

    const defaultColor = availableColors.length > 0 ? availableColors[0].id : 'gray';

    // Get primary image
    const primaryImage = backendProduct.primaryImage ||
      (backendProduct.colorImages?.[0]?.images?.[0] ||
        (backendProduct.images?.[0] || ''));

    return {
      id: backendProduct._id,
      title: backendProduct.name,
      name: backendProduct.name,
      shortDescription: backendProduct.shortDescription || '',
      description: backendProduct.description || '',
      information: backendProduct.information || '',
      price: `${backendProduct.price}.00 TND`,
      img: primaryImage,
      collectionId: backendProduct.collectionId,
      collection: collection || null,
      baseName: 'product',
      availableColors: availableColors.map(c => c.id),
      defaultColor: defaultColor,
      outOfStockColors: [],
      colors: availableColors,
      images: backendProduct.images ? backendProduct.images.map(img => img) : [],
      colorImages: backendProduct.colorImages ? backendProduct.colorImages.map(colorGroup => ({
        ...colorGroup,
        images: colorGroup.images ? colorGroup.images.map(img => img) : []
      })) : [],
      primaryImage: primaryImage,
      originalPrice: backendProduct.price
    };
  };


  if (loading) {
    return <ShopDetailsSkeleton />;
  }

  if (error) {
    return (
      <div className="shop-details-error">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-12 text-center">
              <div className="alert alert-danger">
                <h4>Produit non trouvé</h4>
                <p>{error}</p>
                <button
                  className="btn btn-primary mt-3"
                  onClick={() => history.push('/shop')}
                >
                  Retour à la boutique
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="shop-details-not-found">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-12 text-center">
              <div className="alert alert-warning">
                <h4>Produit non trouvé</h4>
                <p>Le produit demandé n'a pas pu être trouvé.</p>
                <button
                  className="btn btn-primary mt-3"
                  onClick={() => history.push('/shop')}
                >
                  Retour à la boutique
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main>
      <ShopDetailsBreadcumb product={product} collection={collection} />
      <DynamicShopDetailsArea product={product} collection={collection} />
    </main>
  );
}

export default NewShopDetailsPage;